import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translation files directly
import en from './en.json';
import he from './he.json';
import ar from './ar.json';
import ru from './ru.json';

export const LANGUAGE_STORAGE_KEY = 'stock_screener_language';

const SUPPORTED_LANGS = ['en', 'he', 'ar', 'ru'] as const;

// URL override escape hatch. If a user gets trapped in an RTL render bug on
// mobile, opening `?lang=en` or `?resetlang=1` forces English and overwrites
// the stored preference so the next load stays safe.
const readUrlLangOverride = (): string | null => {
  if (typeof window === 'undefined' || !window.location) return null;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('resetlang') === '1') return 'en';
    const lang = (params.get('lang') || '').toLowerCase();
    if (SUPPORTED_LANGS.some((l) => lang === l || lang.startsWith(`${l}-`))) {
      return lang;
    }
  } catch {
    /* malformed query - ignore */
  }
  return null;
};

// Get saved language or default to English
export const getSavedLanguage = (): string => {
  const override = readUrlLangOverride();
  if (override) {
    if (typeof localStorage !== 'undefined') {
      try { localStorage.setItem(LANGUAGE_STORAGE_KEY, override); } catch { /* ignore */ }
    }
    return override;
  }
  if (typeof localStorage === 'undefined') {
    return 'en';
  }
  return localStorage.getItem(LANGUAGE_STORAGE_KEY) || 'en';
};

// Get direction based on language - handles regional codes
// RTL languages: Hebrew (he), Arabic (ar)
export const getDirection = (language: string): 'ltr' | 'rtl' => {
  const rtlLanguages = ['he', 'ar'];
  return rtlLanguages.some(lang => language?.startsWith(lang)) ? 'rtl' : 'ltr';
};

// Initialize i18next with proper resources
i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      he: { translation: he },
      ar: { translation: ar },
      ru: { translation: ru },
    },
    lng: getSavedLanguage(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React already escapes
    },
    // Prevent flash of untranslated content
    react: {
      useSuspense: false,
    },
  });

// Sync language to localStorage when changed
i18n.on('languageChanged', (lng) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  }
  // Update document direction
  const direction = getDirection(lng);
  document.documentElement.lang = lng;
  document.documentElement.dir = direction;
});

export default i18n;
